import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "../lib/reveal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Loader2, ArrowRight, ImagePlus, X, CalendarClock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const segments = [
  { v: "Municipal Corporation", hi: "नगर निगम" },
  { v: "Waste / Bio-mining Operator", hi: "अपशिष्ट / बायो-माइनिंग ऑपरेटर" },
  { v: "Industrial Waste Generator", hi: "औद्योगिक अपशिष्ट उत्पादक" },
  { v: "Consultant / Other", hi: "सलाहकार / अन्य" },
];

const MAX_PHOTOS = 5;
const MAX_BYTES = 12 * 1024 * 1024;

const empty = {
  name: "", email: "", phone: "", organization: "", segment: "", message: "",
  callback_date: "", callback_slot: "",
};

// Time slot options — Mon–Sat, 9:00 AM to 6:00 PM IST, 30-min increments.
const slotOptions = (() => {
  const out = [];
  for (let h = 9; h < 18; h += 1) {
    for (const m of [0, 30]) {
      const label = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      out.push(label);
    }
  }
  return out;
})();

function formatSlotLabel(hhmm, lang) {
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  const en = `${h12}:${String(m).padStart(2, "0")} ${suffix} IST`;
  if (lang !== "hi") return en;
  const hindiSuffix = h >= 12 ? "अपराह्न" : "पूर्वाह्न";
  return `${h12}:${String(m).padStart(2, "0")} ${hindiSuffix} IST`;
}

export const LeadForm = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]); // {storage_path, original_filename, previewUrl, uploading, error}
  const fileInputRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Compute date bounds: today → today + 60 days
  const { minDate, maxDate } = useMemo(() => {
    const today = new Date();
    const min = today.toISOString().slice(0, 10);
    const later = new Date();
    later.setDate(today.getDate() + 60);
    const max = later.toISOString().slice(0, 10);
    return { minDate: min, maxDate: max };
  }, []);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      toast.error(t(`You can attach up to ${MAX_PHOTOS} photos.`, `आप अधिकतम ${MAX_PHOTOS} फ़ोटो जोड़ सकते हैं।`));
      return;
    }
    const toUpload = files.slice(0, remaining);
    for (const file of toUpload) {
      if (!file.type.startsWith("image/")) {
        toast.error(t(`${file.name} is not an image.`, `${file.name} एक चित्र नहीं है।`));
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(t(`${file.name} exceeds 12MB.`, `${file.name} 12MB से अधिक है।`));
        continue;
      }
      const previewUrl = URL.createObjectURL(file);
      const localId = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
      setPhotos((p) => [...p, { localId, previewUrl, original_filename: file.name, uploading: true }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/uploads/site-photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) =>
          p.map((ph) =>
            ph.localId === localId
              ? { ...ph, uploading: false, storage_path: data.storage_path, content_type: data.content_type, size: data.size }
              : ph
          )
        );
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(
          typeof detail === "string"
            ? detail
            : t("Photo upload failed. Please try again.", "फ़ोटो अपलोड विफल रहा। कृपया पुनः प्रयास करें।")
        );
        setPhotos((p) => p.filter((ph) => ph.localId !== localId));
        try { URL.revokeObjectURL(previewUrl); } catch { /* noop */ }
      }
    }
  };

  const removePhoto = (localId) => {
    setPhotos((p) => {
      const target = p.find((x) => x.localId === localId);
      if (target?.previewUrl) {
        try { URL.revokeObjectURL(target.previewUrl); } catch { /* noop */ }
      }
      return p.filter((x) => x.localId !== localId);
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t("Please add your name and email.", "कृपया अपना नाम और ईमेल दर्ज करें।"));
      return;
    }
    if (photos.some((p) => p.uploading)) {
      toast.error(t("Please wait for photos to finish uploading.", "कृपया फ़ोटो अपलोड पूर्ण होने तक प्रतीक्षा करें।"));
      return;
    }
    if ((form.callback_date && !form.callback_slot) || (!form.callback_date && form.callback_slot)) {
      toast.error(t("Please pick both a date and a time slot for the callback.", "कृपया कॉलबैक के लिए दिनांक और समय स्लॉट दोनों चुनें।"));
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (!payload[k]) delete payload[k]; });
      const attachedPhotos = photos
        .filter((p) => p.storage_path)
        .map((p) => ({
          storage_path: p.storage_path,
          original_filename: p.original_filename,
          content_type: p.content_type,
          size: p.size,
        }));
      if (attachedPhotos.length) payload.photos = attachedPhotos;
      payload.lang = lang;
      await axios.post(`${API}/leads`, payload);
      toast.success(t("Thank you — our team will be in touch shortly.", "धन्यवाद — हमारी टीम शीघ्र ही आपसे संपर्क करेगी।"));
      // cleanup previews
      photos.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      setPhotos([]);
      setForm(empty);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("Something went wrong. Please try again.", "कुछ गड़बड़ हो गई। कृपया पुनः प्रयास करें।"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent rounded-none border-0 border-b border-stone/30 py-3 text-lg text-ink placeholder:text-stone/70 focus:outline-none focus:border-moss transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal className="flex items-center gap-3 mb-6">
              <span className="text-xs tracking-[0.22em] uppercase font-semibold text-moss">{t("Request a Pilot", "पायलट का अनुरोध करें")}</span>
            </Reveal>
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.0] text-ink">
                {t("Start with a low-risk pilot.", "एक कम-जोखिम वाले पायलट से शुरुआत करें।")}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-slate max-w-md">
                {t(
                  "Tell us about your site. Share a few photos and pick a callback slot — we'll design a pilot with site analysis, a treatment plan and performance reporting.",
                  "अपनी साइट के बारे में बताएं। कुछ फ़ोटो साझा करें और कॉलबैक स्लॉट चुनें — हम साइट विश्लेषण, उपचार योजना और प्रदर्शन रिपोर्टिंग के साथ एक पायलट तैयार करेंगे।"
                )}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 space-y-4">
                <a href="tel:9899534141" data-testid="contact-phone" className="block font-serif text-3xl text-ink hover:text-moss transition-colors duration-300">
                  +91 98995 34141
                </a>
                <a href="mailto:care@fresh-earth.com" data-testid="contact-email" className="block font-serif italic text-2xl text-moss hover:text-terracotta transition-colors duration-300">
                  care@fresh-earth.com
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} data-testid="lead-form" className="bg-sand rounded-3xl border border-stone/20 p-8 md:p-12 space-y-7">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                  <div>
                    <label className="text-xs tracking-[0.14em] uppercase text-stone">{t("Name *", "नाम *")}</label>
                    <input data-testid="lead-name" value={form.name} onChange={set("name")} placeholder={t("Your full name", "आपका पूरा नाम")} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.14em] uppercase text-stone">{t("Email *", "ईमेल *")}</label>
                    <input data-testid="lead-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.14em] uppercase text-stone">{t("Phone", "फ़ोन")}</label>
                    <input data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder={t("Contact number", "संपर्क नंबर")} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.14em] uppercase text-stone">{t("Organization", "संगठन")}</label>
                    <input data-testid="lead-org" value={form.organization} onChange={set("organization")} placeholder={t("City / company", "शहर / कंपनी")} className={inputCls} />
                  </div>
                </div>

                <div>
                  <label className="text-xs tracking-[0.14em] uppercase text-stone">{t("You are a", "आप हैं")}</label>
                  <Select value={form.segment} onValueChange={(v) => setForm((f) => ({ ...f, segment: v }))}>
                    <SelectTrigger data-testid="lead-segment" className="w-full bg-transparent rounded-none border-0 border-b border-stone/30 px-0 py-3 h-auto text-lg text-ink focus:ring-0 focus:border-moss [&>span]:text-ink data-[placeholder]:[&>span]:text-stone/70">
                      <SelectValue placeholder={t("Select a segment", "एक वर्ग चुनें")} />
                    </SelectTrigger>
                    <SelectContent className="bg-bone border-stone/20">
                      {segments.map((s) => (
                        <SelectItem key={s.v} value={s.v} data-testid={`segment-${s.v}`}>{t(s.v, s.hi)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Callback slot picker */}
                <div className="pt-2 border-t border-stone/20">
                  <div className="flex items-center gap-2 mb-4">
                    <CalendarClock size={16} className="text-moss" />
                    <span className="text-xs tracking-[0.14em] uppercase text-stone">
                      {t("Officer Callback (optional)", "अधिकारी कॉलबैक (वैकल्पिक)")}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                    <div>
                      <label className="text-xs tracking-[0.14em] uppercase text-stone">{t("Preferred date", "पसंदीदा दिनांक")}</label>
                      <input
                        data-testid="lead-callback-date"
                        type="date"
                        value={form.callback_date}
                        min={minDate}
                        max={maxDate}
                        onChange={set("callback_date")}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className="text-xs tracking-[0.14em] uppercase text-stone">{t("Time slot (IST)", "समय स्लॉट (IST)")}</label>
                      <Select value={form.callback_slot} onValueChange={(v) => setForm((f) => ({ ...f, callback_slot: v }))}>
                        <SelectTrigger data-testid="lead-callback-slot" className="w-full bg-transparent rounded-none border-0 border-b border-stone/30 px-0 py-3 h-auto text-lg text-ink focus:ring-0 focus:border-moss [&>span]:text-ink data-[placeholder]:[&>span]:text-stone/70">
                          <SelectValue placeholder={t("Choose a 30-min slot", "30-मिनट का स्लॉट चुनें")} />
                        </SelectTrigger>
                        <SelectContent className="bg-bone border-stone/20 max-h-64">
                          {slotOptions.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`slot-${s}`}>
                              {formatSlotLabel(s, lang)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-stone">
                    {t("Mon–Sat · 9:00 AM – 6:00 PM IST · we'll send a confirmation email with a calendar invite", "सोम–शनि · प्रातः 9:00 – सायं 6:00 IST · पुष्टि ईमेल व कैलेंडर आमंत्रण भेजा जाएगा")}
                  </p>
                </div>

                {/* Site photo upload */}
                <div className="pt-2 border-t border-stone/20">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <ImagePlus size={16} className="text-moss" />
                      <span className="text-xs tracking-[0.14em] uppercase text-stone">
                        {t("Site Photos (optional)", "साइट फ़ोटो (वैकल्पिक)")}
                      </span>
                    </div>
                    <span className="text-xs text-stone">{photos.length}/{MAX_PHOTOS}</span>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    data-testid="lead-photos-input"
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                  />

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {photos.map((p) => (
                      <div key={p.localId} className="relative aspect-square rounded-2xl overflow-hidden border border-stone/25 bg-bone" data-testid={`lead-photo-${p.localId}`}>
                        <img src={p.previewUrl} alt={p.original_filename} className="w-full h-full object-cover" />
                        {p.uploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-ink/40 text-bone">
                            <Loader2 size={20} className="animate-spin" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removePhoto(p.localId)}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-ink/70 hover:bg-ink text-bone flex items-center justify-center transition-colors"
                          aria-label={t("Remove photo", "फ़ोटो हटाएँ")}
                          data-testid={`remove-photo-${p.localId}`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    {photos.length < MAX_PHOTOS && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="add-photo-btn"
                        className="aspect-square rounded-2xl border border-dashed border-stone/40 text-stone hover:border-moss hover:text-moss transition-colors flex flex-col items-center justify-center gap-2 bg-bone/40"
                      >
                        <ImagePlus size={22} />
                        <span className="text-xs tracking-[0.12em] uppercase">{t("Add photo", "फ़ोटो जोड़ें")}</span>
                      </button>
                    )}
                  </div>
                  <p className="mt-3 text-xs text-stone">
                    {t("JPG, PNG or WEBP · up to 12MB each", "JPG, PNG या WEBP · प्रत्येक 12MB तक")}
                  </p>
                </div>

                <div>
                  <label className="text-xs tracking-[0.14em] uppercase text-stone">{t("Message", "संदेश")}</label>
                  <textarea data-testid="lead-message" value={form.message} onChange={set("message")} rows={3} placeholder={t("Tell us about your site or challenge", "अपनी साइट या चुनौती के बारे में बताएं")} className={`${inputCls} resize-none`} />
                </div>

                <button
                  type="submit"
                  data-testid="lead-submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-2 rounded-full bg-forest text-bone text-sm tracking-[0.1em] uppercase px-8 py-4 hover:bg-moss transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                  {loading ? t("Sending...", "भेजा जा रहा है...") : t("Request a Pilot", "पायलट का अनुरोध करें")}
                  {!loading && <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
